/**
 * Published cutthe6.com prices — the single frontend source for the numbers on
 * the pricing page, the publish-payment modal, and the seller draft banner.
 *
 * Source: the product pricing sheet (2026-09-27). The listing fee must match
 * the backend charge in `backend/app/services/billing_service.py`
 * (`full_service_amount_cents`): $799, or $1,499 when the list price is
 * $1,000,000 or more. It is a flat software fee charged when the seller
 * publishes — never a percentage of the sale price and never charged at close.
 */

/** Standard listing fee (cents) — homes listed under $1,000,000. */
export const LISTING_FEE_CENTS = 79900;

/** Listing fee (cents) for homes listed at $1,000,000 or more. */
export const LISTING_FEE_PREMIUM_CENTS = 149900;

/** List price (dollars) at which the premium listing fee applies. */
export const LISTING_FEE_PREMIUM_MIN_PRICE = 1_000_000;

/**
 * Buyer paperwork fee (cents) for a home that is NOT listed on cutthe6.com, per
 * the pricing sheet. Not quoted in the UI: Buyer Paperwork is presented as part
 * of buying on cutthe6.com.
 */
export const BUYER_PAPERWORK_FEE_CENTS = 49900;

/**
 * Listing-side commission rate for the public savings comparisons.
 *
 * The flat fee replaces what the seller's own listing agent would charge, so
 * that is what the comparison uses, not the combined 5–6% (which includes the
 * buyer's side, a cost an owner-seller may still face if the buyer has an
 * agent). Listing-side rates are commonly quoted around 2.5–3%; 2.5% is the
 * low end, so the savings figure errs low.
 */
export const LISTING_SIDE_COMMISSION_RATE = 0.025;

/** The rate above as display text, e.g. "2.5%". */
export const LISTING_SIDE_COMMISSION_LABEL = `${(LISTING_SIDE_COMMISSION_RATE * 100).toLocaleString('en-US')}%`;

/**
 * Estimated listing-side commission on a sale price.
 *
 * @param {number|string|null|undefined} price - Sale price in dollars.
 * @returns {number|null} Whole dollars, or null for a missing/invalid price.
 */
export function listingSideCommission(price) {
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return Math.round(amount * LISTING_SIDE_COMMISSION_RATE);
}

/**
 * Listing fee for a list price, mirroring the backend tier rule.
 *
 * @param {number|string|null|undefined} listPrice - Asking price in dollars.
 *   Missing or unparseable prices fall back to the standard fee.
 * @returns {number} Fee in cents.
 */
export function listingFeeCents(listPrice) {
  const price = Number(listPrice);
  if (Number.isFinite(price) && price >= LISTING_FEE_PREMIUM_MIN_PRICE) {
    return LISTING_FEE_PREMIUM_CENTS;
  }
  return LISTING_FEE_CENTS;
}

/**
 * Format integer cents as whole US dollars (e.g. 149900 → "$1,499").
 *
 * @param {number} cents
 * @returns {string}
 */
export function formatDollars(cents) {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}
